import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { useToast } from '../../context/ToastContext';
import Header from '../../components/common/Header';
import Sidebar from '../../components/common/Sidebar';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { HelpCircle, Plus, Building2, X, Edit2, Trash2 } from 'lucide-react';
import './AdminPages.css';

const AdminQuestions = () => {
  const [mcqs, setMcqs] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [selectedDeptId, setSelectedDeptId] = useState('');
  const [deptQs, setDeptQs] = useState([]);
  const [loading, setLoading] = useState(true);

  // Modals & Editing State
  const [showMcqModal, setShowMcqModal] = useState(false);
  const [editingMcq, setEditingMcq] = useState(null);

  const [showDeptQModal, setShowDeptQModal] = useState(false);
  const [editingDeptQ, setEditingDeptQ] = useState(null);

  const [mcqForm, setMcqForm] = useState({
    question_order_number: 1,
    question: '',
    option_a: '',
    option_b: '',
    option_c: '',
    option_d: ''
  });

  const [deptQForm, setDeptQForm] = useState({
    department: '',
    question_number: 1,
    question: '',
    option_a: '',
    option_b: '',
    option_c: '',
    option_d: ''
  });

  const { addToast } = useToast();

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    if (selectedDeptId) {
      fetchDeptQuestions(selectedDeptId);
    }
  }, [selectedDeptId]);

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      const [mcqRes, deptRes] = await Promise.all([
        api.get('/onboarding/questions/'),
        api.get('/departments/')
      ]);
      setMcqs(mcqRes.data.results || mcqRes.data);
      const deptList = deptRes.data.results || deptRes.data;
      setDepartments(deptList);
      if (deptList.length > 0) {
        setSelectedDeptId(deptList[0].id);
      }
    } catch (err) {
      console.error(err);
      addToast('Failed to load questions.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchDeptQuestions = async (deptId) => {
    try {
      const res = await api.get(`/department-questions/?department_id=${deptId}`);
      setDeptQs(res.data.results || res.data);
    } catch (err) {
      console.error(err);
    }
  };

  // Predefined MCQ Handlers
  const openCreateMcqModal = () => {
    setEditingMcq(null);
    setMcqForm({
      question_order_number: (mcqs.length + 1) || 1,
      question: '',
      option_a: '',
      option_b: '',
      option_c: '',
      option_d: ''
    });
    setShowMcqModal(true);
  };

  const openEditMcqModal = (q) => {
    setEditingMcq(q);
    setMcqForm({
      question_order_number: q.question_order_number,
      question: q.question,
      option_a: q.option_a,
      option_b: q.option_b,
      option_c: q.option_c,
      option_d: q.option_d
    });
    setShowMcqModal(true);
  };

  const handleSaveMcq = async (e) => {
    e.preventDefault();
    try {
      if (editingMcq) {
        await api.put(`/onboarding/questions/${editingMcq.id}/`, mcqForm);
        addToast('Predefined MCQ Question updated!', 'success');
      } else {
        await api.post('/onboarding/questions/', mcqForm);
        addToast('Predefined MCQ Question created!', 'success');
      }
      setShowMcqModal(false);
      setEditingMcq(null);
      fetchInitialData();
    } catch (err) {
      console.error(err);
      addToast('Failed to save MCQ question.', 'error');
    }
  };

  const handleDeleteMcq = async (id) => {
    if (!window.confirm('Are you sure you want to delete this predefined MCQ?')) return;
    try {
      await api.delete(`/onboarding/questions/${id}/`);
      addToast('Predefined MCQ Question deleted!', 'success');
      fetchInitialData();
    } catch (err) {
      console.error(err);
      addToast('Failed to delete MCQ question.', 'error');
    }
  };

  // Department Question Handlers
  const openCreateDeptQModal = () => {
    setEditingDeptQ(null);
    const currentDeptId = selectedDeptId || (departments.length > 0 ? departments[0].id : '');
    setDeptQForm({
      department: currentDeptId,
      question_number: (deptQs.length + 1) || 1,
      question: '',
      option_a: '',
      option_b: '',
      option_c: '',
      option_d: ''
    });
    setShowDeptQModal(true);
  };

  const openEditDeptQModal = (q) => {
    setEditingDeptQ(q);
    setDeptQForm({
      department: q.department,
      question_number: q.question_number,
      question: q.question,
      option_a: q.option_a,
      option_b: q.option_b,
      option_c: q.option_c,
      option_d: q.option_d
    });
    setShowDeptQModal(true);
  };

  const handleSaveDeptQ = async (e) => {
    e.preventDefault();
    if (!deptQForm.department) {
      addToast('Please select a department.', 'error');
      return;
    }
    try {
      if (editingDeptQ) {
        await api.put(`/department-questions/${editingDeptQ.id}/`, deptQForm);
        addToast('Department Question updated!', 'success');
      } else {
        await api.post('/department-questions/', deptQForm);
        addToast('Department Question created!', 'success');
      }
      setShowDeptQModal(false);
      setEditingDeptQ(null);
      fetchDeptQuestions(deptQForm.department);
    } catch (err) {
      console.error(err);
      addToast(err.response?.data?.message || 'Failed to save department question.', 'error');
    }
  };

  const handleDeleteDeptQ = async (id) => {
    if (!window.confirm('Are you sure you want to delete this department question?')) return;
    try {
      await api.delete(`/department-questions/${id}/`);
      addToast('Department Question deleted!', 'success');
      fetchDeptQuestions(selectedDeptId);
    } catch (err) {
      console.error(err);
      addToast('Failed to delete department question.', 'error');
    }
  };

  return (
    <div className="page-layout-root">
      <Header />
      <div className="page-body-wrapper">
        <Sidebar />
        <main className="page-main-content">
          
          <div className="admin-page-header">
            <div>
              <h1 className="admin-page-title">Onboarding Question Bank Management</h1>
              <p className="admin-page-subtitle">Configure general predefined MCQs and department-specific assessment questions.</p>
            </div>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button className="btn btn-secondary" onClick={openCreateMcqModal}>
                <Plus size={16} /> Add Predefined MCQ
              </button>
              <button className="btn btn-primary" onClick={openCreateDeptQModal}>
                <Plus size={16} /> Add Department Question
              </button>
            </div>
          </div>

          {loading ? <LoadingSpinner label="Loading questions..." /> : (
            <div className="admin-grid-2col">
              
              {/* Predefined MCQs */}
              <div className="card" style={{ padding: '28px' }}>
                <h2 style={{ fontSize: '1.2rem', fontWeight: 800, marginBottom: '20px', color: '#818cf8', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <HelpCircle size={20} /> Predefined Sequential MCQs ({mcqs.length})
                </h2>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                  {mcqs.length === 0 ? (
                    <div style={{ fontSize: '0.88rem', color: '#64748b', textAlign: 'center', padding: '24px' }}>
                      No predefined MCQs configured yet.
                    </div>
                  ) : (
                    mcqs.map(q => (
                      <div key={q.id} className="admin-item-row" style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px' }}>
                          <div style={{ fontWeight: 700, fontSize: '0.96rem', color: '#f8fafc', lineHeight: 1.45 }}>
                            <span style={{ color: '#818cf8', marginRight: '6px' }}>Q{q.question_order_number}.</span>
                            {q.question}
                          </div>
                          <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
                            <button
                              type="button"
                              className="btn btn-sm btn-secondary"
                              style={{ padding: '6px 12px', fontSize: '0.8rem', display: 'inline-flex', alignItems: 'center', gap: '6px' }}
                              onClick={() => openEditMcqModal(q)}
                              title="Edit Question"
                            >
                              <Edit2 size={13} /> Edit
                            </button>
                            <button
                              type="button"
                              className="btn btn-sm"
                              style={{ padding: '6px 10px', fontSize: '0.8rem', background: 'rgba(239, 68, 68, 0.15)', color: '#f87171', border: '1px solid rgba(239, 68, 68, 0.3)' }}
                              onClick={() => handleDeleteMcq(q.id)}
                              title="Delete Question"
                            >
                              <Trash2 size={13} />
                            </button>
                          </div>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '0.82rem', color: '#94a3b8', background: 'rgba(15, 23, 42, 0.6)', padding: '10px 14px', borderRadius: '10px', border: '1px solid rgba(255, 255, 255, 0.05)' }}>
                          <div><strong style={{ color: '#cbd5e1' }}>A:</strong> {q.option_a}</div>
                          <div><strong style={{ color: '#cbd5e1' }}>B:</strong> {q.option_b}</div>
                          <div><strong style={{ color: '#cbd5e1' }}>C:</strong> {q.option_c}</div>
                          <div><strong style={{ color: '#cbd5e1' }}>D:</strong> {q.option_d}</div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Department Questions */}
              <div className="card" style={{ padding: '28px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px', flexWrap: 'wrap', gap: '10px' }}>
                  <h2 style={{ fontSize: '1.2rem', fontWeight: 800, color: '#34d399', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Building2 size={20} /> Department Questions ({deptQs.length})
                  </h2>
                  <select
                    className="form-select"
                    style={{ width: '190px', padding: '8px 12px', fontSize: '0.85rem' }}
                    value={selectedDeptId}
                    onChange={e => setSelectedDeptId(e.target.value)}
                  >
                    {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                  </select>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                  {deptQs.length === 0 ? (
                    <div style={{ fontSize: '0.88rem', color: '#64748b', textAlign: 'center', padding: '24px' }}>
                      No questions configured for this department yet.
                    </div>
                  ) : (
                    deptQs.map(q => (
                      <div key={q.id} className="admin-item-row" style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px' }}>
                          <div style={{ fontWeight: 700, fontSize: '0.96rem', color: '#f8fafc', lineHeight: 1.45 }}>
                            <span style={{ color: '#34d399', marginRight: '6px' }}>Q{q.question_number}.</span>
                            {q.question}
                          </div>
                          <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
                            <button
                              type="button"
                              className="btn btn-sm btn-secondary"
                              style={{ padding: '6px 12px', fontSize: '0.8rem', display: 'inline-flex', alignItems: 'center', gap: '6px' }}
                              onClick={() => openEditDeptQModal(q)}
                              title="Edit Question"
                            >
                              <Edit2 size={13} /> Edit
                            </button>
                            <button
                              type="button"
                              className="btn btn-sm"
                              style={{ padding: '6px 10px', fontSize: '0.8rem', background: 'rgba(239, 68, 68, 0.15)', color: '#f87171', border: '1px solid rgba(239, 68, 68, 0.3)' }}
                              onClick={() => handleDeleteDeptQ(q.id)}
                              title="Delete Question"
                            >
                              <Trash2 size={13} />
                            </button>
                          </div>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '0.82rem', color: '#94a3b8', background: 'rgba(15, 23, 42, 0.6)', padding: '10px 14px', borderRadius: '10px', border: '1px solid rgba(255, 255, 255, 0.05)' }}>
                          <div><strong style={{ color: '#cbd5e1' }}>A:</strong> {q.option_a}</div>
                          <div><strong style={{ color: '#cbd5e1' }}>B:</strong> {q.option_b}</div>
                          <div><strong style={{ color: '#cbd5e1' }}>C:</strong> {q.option_c}</div>
                          <div><strong style={{ color: '#cbd5e1' }}>D:</strong> {q.option_d}</div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>

            </div>
          )}

          {/* Modal: Create / Edit MCQ */}
          {showMcqModal && (
            <div className="modal-backdrop">
              <div className="modal-content" style={{ padding: '32px', maxWidth: '580px' }}>
                <div className="admin-modal-header">
                  <h2 className="admin-modal-title">{editingMcq ? 'Edit Predefined MCQ' : 'Create Predefined MCQ'}</h2>
                  <button onClick={() => { setShowMcqModal(false); setEditingMcq(null); }}><X size={20} /></button>
                </div>
                <form onSubmit={handleSaveMcq}>
                  <div className="form-group">
                    <label className="form-label">Order Number *</label>
                    <input
                      type="number"
                      className="form-input"
                      required
                      min={1}
                      value={mcqForm.question_order_number}
                      onChange={e => setMcqForm({ ...mcqForm, question_order_number: parseInt(e.target.value) || 1 })}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Question Text *</label>
                    <input
                      type="text"
                      className="form-input"
                      required
                      placeholder="Enter question text..."
                      value={mcqForm.question}
                      onChange={e => setMcqForm({ ...mcqForm, question: e.target.value })}
                    />
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginTop: '14px' }}>
                    <div className="form-group">
                      <label className="form-label">Option A *</label>
                      <input type="text" className="form-input" required placeholder="Option A" value={mcqForm.option_a} onChange={e => setMcqForm({ ...mcqForm, option_a: e.target.value })} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Option B *</label>
                      <input type="text" className="form-input" required placeholder="Option B" value={mcqForm.option_b} onChange={e => setMcqForm({ ...mcqForm, option_b: e.target.value })} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Option C *</label>
                      <input type="text" className="form-input" required placeholder="Option C" value={mcqForm.option_c} onChange={e => setMcqForm({ ...mcqForm, option_c: e.target.value })} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Option D *</label>
                      <input type="text" className="form-input" required placeholder="Option D" value={mcqForm.option_d} onChange={e => setMcqForm({ ...mcqForm, option_d: e.target.value })} />
                    </div>
                  </div>
                  <div className="admin-modal-actions">
                    <button type="button" className="btn btn-secondary" onClick={() => { setShowMcqModal(false); setEditingMcq(null); }}>Cancel</button>
                    <button type="submit" className="btn btn-primary">{editingMcq ? 'Update Question' : 'Save Question'}</button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Modal: Create / Edit Dept Q */}
          {showDeptQModal && (
            <div className="modal-backdrop">
              <div className="modal-content" style={{ padding: '32px', maxWidth: '580px' }}>
                <div className="admin-modal-header">
                  <h2 className="admin-modal-title">{editingDeptQ ? 'Edit Department Question' : 'Add Department Question'}</h2>
                  <button onClick={() => { setShowDeptQModal(false); setEditingDeptQ(null); }}><X size={20} /></button>
                </div>
                <form onSubmit={handleSaveDeptQ}>
                  <div className="form-group">
                    <label className="form-label">Department *</label>
                    <select
                      className="form-select"
                      required
                      value={deptQForm.department}
                      onChange={e => setDeptQForm({ ...deptQForm, department: e.target.value })}
                    >
                      <option value="">Select Department...</option>
                      {departments.map(d => (
                        <option key={d.id} value={d.id}>{d.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Question Number *</label>
                    <input
                      type="number"
                      className="form-input"
                      required
                      min={1}
                      value={deptQForm.question_number}
                      onChange={e => setDeptQForm({ ...deptQForm, question_number: parseInt(e.target.value) || 1 })}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Question Text *</label>
                    <input
                      type="text"
                      className="form-input"
                      required
                      value={deptQForm.question}
                      onChange={e => setDeptQForm({ ...deptQForm, question: e.target.value })}
                      placeholder="Enter assessment question text..."
                    />
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginTop: '14px' }}>
                    <div className="form-group">
                      <label className="form-label">Option A *</label>
                      <input type="text" className="form-input" required placeholder="Option A" value={deptQForm.option_a} onChange={e => setDeptQForm({ ...deptQForm, option_a: e.target.value })} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Option B *</label>
                      <input type="text" className="form-input" required placeholder="Option B" value={deptQForm.option_b} onChange={e => setDeptQForm({ ...deptQForm, option_b: e.target.value })} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Option C *</label>
                      <input type="text" className="form-input" required placeholder="Option C" value={deptQForm.option_c} onChange={e => setDeptQForm({ ...deptQForm, option_c: e.target.value })} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Option D *</label>
                      <input type="text" className="form-input" required placeholder="Option D" value={deptQForm.option_d} onChange={e => setDeptQForm({ ...deptQForm, option_d: e.target.value })} />
                    </div>
                  </div>
                  <div className="admin-modal-actions">
                    <button type="button" className="btn btn-secondary" onClick={() => { setShowDeptQModal(false); setEditingDeptQ(null); }}>Cancel</button>
                    <button type="submit" className="btn btn-primary">{editingDeptQ ? 'Update Question' : 'Save Question'}</button>
                  </div>
                </form>
              </div>
            </div>
          )}

        </main>
      </div>
    </div>
  );
};

export default AdminQuestions;
